import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { Button, ButtonGroup, ButtonToolbar } from 'reactstrap';
import { FormattedMessage } from 'react-intl';

import { APIConfig } from 'src/config';
import {
  FEATURE_INHIBIT_CONTRACTED_TRADES, FEATURE_INHIBIT_NOMINATED_TRADES,
  FEATURE_INHIBIT_COMMUNITY_TRADES, LIST_FILTER_ALL, TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
  TRADE_TYPE_CONTRACTED, TRADE_TYPE_NOMINATED, TRADE_TYPE_COMMUNITY, TRADE_TYPE_RESIDUAL,
} from 'src/util/constants';
import { getDefaultLocale, getLocale } from 'src/util/i18n/handler';

/**
 * Control/Filter component for the trade list table
 * @param {object} root0
 * @param {DateTime} root0.date - date selected (to view the trades)
 * @param {Function} root0.dateUpdateFunc - to update the user date selection
 * @param {object} root0.filter - object contains tradeDirection and tradeType
 * @param {Function} root0.filterFunc - update the filter selection
 * @param {boolean} root0.isModalOpen - indicate whether the loading modal is open or not
 * @returns {React.ReactComponentElement} -TradeListControls component
 */
function TradeListControls({
  date, dateUpdateFunc, filter, filterFunc, isModalOpen,
}) {
  const [selectedDate, setSelectedDate] = useState(date);
  const [myRef, setMyRef] = useState(false);

  const closeCalendar = () => {
    myRef.setOpen(false);
  };

  const defaultLocale = getDefaultLocale(getLocale());

  const dateButtonLabel = (d) => (d.setLocale(defaultLocale).toFormat('d MMM yyyy'));

  const setDate = () => {
    dateUpdateFunc(selectedDate);
  };

  const updateDate = (d) => {
    const newDate = d && DateTime.fromJSDate(d);
    if (newDate && date && newDate.toSeconds() === date.toSeconds()) {
      return;
    }
    setSelectedDate(newDate);
  };

  const renderDatePicker = (onChangeFunc, onCloseFunc) => (
    <DatePicker
      locale={defaultLocale}
      ref={(r) => { setMyRef(r); }}
      value={dateButtonLabel(date)}
      selected={selectedDate ? selectedDate.toJSDate() : null}
      onChange={onChangeFunc}
      onCalendarClose={onCloseFunc}
      customInput={<DateButton isModalOpen={isModalOpen} />}
      shouldCloseOnSelect={false}
      popperPlacement="bottom-end"
      popperModifiers={{
        preventOverflow: {
          enabled: true,
          escapeWithReference: false,
          boundariesElement: 'viewport',
        },
      }}
    >
      <div
        style={{
          clear: 'both',
          textAlign: 'right',
          borderTop: '1px solid #ccc',
          padding: '1em',
        }}
      >
        <button className="btn btn-primary" type="button" onClick={() => { setDate(); closeCalendar(); }}>
          <FormattedMessage
            id="trade.trade_list.trade_list_controls.date_picker.apply_button.label"
            defaultMessage="Apply"
          />
        </button>
      </div>
    </DatePicker>
  );

  const filterIncludes = (key, value) => (filter && filter[key] && filter[key].includes(value));

  return (
    <div className="trade-history-filters">
      <ButtonToolbar>
        <ButtonGroup className="me-2 mb-2">
          <Button size="sm" className="btn btn-darken trades-history-all" active={!filter.direction && !filter.type} onClick={() => filterFunc(null, LIST_FILTER_ALL)}>
            <FormattedMessage id="trade.trade_list.trade_list_controls.all.label" defaultMessage="All" />
          </Button>
        </ButtonGroup>

        <ButtonGroup className="me-2 mb-2">
          <Button size="sm" className="btn btn-darken trades-history-buy" active={filterIncludes('direction', TRADE_DIRECTION_BUY)} onClick={() => filterFunc('direction', TRADE_DIRECTION_BUY)}>
            <FormattedMessage id="trade.trade_list.trade_list_controls.buy.label" defaultMessage="Buy" />
          </Button>
          <Button size="sm" className="btn btn-darken trades-history-sell" active={filterIncludes('direction', TRADE_DIRECTION_SELL)} onClick={() => filterFunc('direction', TRADE_DIRECTION_SELL)}>
            <FormattedMessage id="trade.trade_list.trade_list_controls.sell.label" defaultMessage="Sell" />
          </Button>
        </ButtonGroup>

        <ButtonGroup className="me-2 mb-2">
          {!APIConfig().feature(FEATURE_INHIBIT_CONTRACTED_TRADES) && (
            <Button size="sm" className="btn btn-darken trades-history-contracted" active={filterIncludes('type', TRADE_TYPE_CONTRACTED)} onClick={() => filterFunc('type', TRADE_TYPE_CONTRACTED)}>
              <FormattedMessage id="trade.trade_list.trade_list_controls.contracted.label" defaultMessage="Contracted" />
            </Button>
          )}
          {!APIConfig().feature(FEATURE_INHIBIT_NOMINATED_TRADES) && (
            <Button size="sm" className="btn btn-darken trades-history-nominated" active={filterIncludes('type', TRADE_TYPE_NOMINATED)} onClick={() => filterFunc('type', TRADE_TYPE_NOMINATED)}>
              <FormattedMessage id="trade.trade_list.trade_list_controls.nominated.label" defaultMessage="Peer-to-peer" />
            </Button>
          )}
          {!APIConfig().feature(FEATURE_INHIBIT_COMMUNITY_TRADES) && (
            <Button size="sm" className="btn btn-darken trades-history-community" active={filterIncludes('type', TRADE_TYPE_COMMUNITY)} onClick={() => filterFunc('type', TRADE_TYPE_COMMUNITY)}>
              <FormattedMessage id="trade.trade_list.trade_list_controls.community.label" defaultMessage="Community" />
            </Button>
          )}
          <Button size="sm" className="btn btn-darken trades-history-default" active={filterIncludes('type', TRADE_TYPE_RESIDUAL)} onClick={() => filterFunc('type', TRADE_TYPE_RESIDUAL)}>
            <FormattedMessage id="trade.trade_list.trade_list_controls.retailer_default.label" defaultMessage="Retailer default" />
          </Button>
        </ButtonGroup>

        <ButtonGroup className="me-2 mb-2 trades-history-date-picker">
          {renderDatePicker(updateDate, setDate)}
        </ButtonGroup>
      </ButtonToolbar>
    </div>
  );
}

TradeListControls.propTypes = {
  date: PropTypes.instanceOf(DateTime).isRequired,
  dateUpdateFunc: PropTypes.func.isRequired,
  filter: PropTypes.shape({
    direction: PropTypes.arrayOf(PropTypes.string),
    type: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  filterFunc: PropTypes.func.isRequired,
  isModalOpen: PropTypes.bool,
};

TradeListControls.defaultProps = {
  isModalOpen: false,
};

export default TradeListControls;

// Ref: <https://github.com/Hacker0x01/react-datepicker/issues/862>
//
// eslint-disable-next-line react/prefer-stateless-function
class DateButton extends React.Component {
  render() {
    const {
      onClick,
      disabled,
      isModalOpen,
      value,
    } = this.props;

    return (
      <Button size="sm" className="btn btn-darken" onClick={() => (onClick())} disabled={disabled || isModalOpen}>
        {value}
      </Button>
    );
  }
}

DateButton.propTypes = {
  disabled: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  onClick: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
};
DateButton.defaultProps = {
  disabled: false,
  isModalOpen: false,
  onClick: null,
  value: <FormattedMessage id="trade.trade_list.trade_list_controls.date_button.default_value.label" defaultMessage="loading..." />,
};

export { DateButton };
